/**
 * API service for invoice generation operations
 * Follows the same pattern as TicketValidator API calls
 */

import { getConfig } from './config';
import { authenticatedFetch } from './authService';

/**
 * Generate invoice using the backend API
 * @param {Object} saleData - Sale information
 * @param {Object} customerData - Customer information  
 * @returns {Promise<Object>} Invoice generation result
 */
export const generateInvoice = async (saleData, customerData) => {
    const config = getConfig();
    const API_BASE_URL = config.apiBaseUrl;
    
    // Log API request for debugging
    console.log(`API Request to: ${API_BASE_URL}/facturas`);
    console.log('Sale data:', JSON.stringify(saleData, null, 2));
    console.log('=== FORMA PAGO DEBUG ===');
    console.log('saleData.formaPago:', saleData.formaPago);
    console.log('Customer data:', JSON.stringify(customerData, null, 2));
    
    try {
        // Prepare the request payload according to API documentation
        const requestPayload = {
            sucursal_id: saleData.sucursalId,
            venta_id: saleData.id,
            total: saleData.total, // Include the total from the sale data
            cliente: {
                id: customerData.id || null,
                rfc: customerData.rfc,
                razon_social: customerData.razonSocial,
                email: customerData.email || '',
                cp_fiscal: customerData.codigoPostal,
                uso_cfdi: customerData.usoCfdi || 'G03',
                regimen_fiscal: customerData.regimenFiscal || '601'
            },
            forma_pago: saleData.formaPago || '99', // Add forma_pago field from the new API schema
            productos: saleData.productos.map(producto => {
                console.log('Processing product for invoice:', JSON.stringify(producto, null, 2));
                const mappedProduct = {
                    id: producto.id,
                    cantidad: producto.cantidad,
                    producto: producto.nombre,
                    precio_unitario: producto.precioUnitario,
                    importe: producto.importe || producto.subtotal,
                    descuento: parseFloat(producto.descuento) || 0,
                    descripcion: producto.nombre,
                    // Add the new SAT catalog fields
                    clave_unidad: producto.claveUnidad || 'H87',
                    clave_unidad_descripcion: producto.claveUnidadDescripcion || 'piece',
                    clave_prod_serv: producto.claveProdServ || '01010101',
                    producto_servicio: producto.productoServicio || 'No Existe en el Catálogo'
                };
                console.log(`Product ${producto.id} mapped with descuento: ${mappedProduct.descuento}`);
                return mappedProduct;
            })
        };
        
        console.log('Request payload:', JSON.stringify(requestPayload, null, 2));
        
        const response = await authenticatedFetch(`${API_BASE_URL}/facturas`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestPayload)
        });
        
        // Handle different response status codes
        if (response.status === 400) {
            const errorData = await response.json();
            return {
                success: false,
                error: errorData.message || 'Error de validación en los datos fiscales',
                detailedError: errorData.error || null
            };
        }
        
        if (response.status === 409) {
            const errorData = await response.json();
            const existingInvoice = errorData.existing_invoice;
            const errorMessage = errorData.message || 'Esta venta ya ha sido facturada';
            
            // If we have existing invoice info, provide more details
            if (existingInvoice && existingInvoice.uuid) {
                return {
                    success: false,
                    error: `${errorMessage}. UUID: ${existingInvoice.uuid}`,
                    isDuplicate: true,
                    existingInvoice: existingInvoice
                };
            }
            
            return {
                success: false,
                error: errorMessage,
                isDuplicate: true
            };
        }
        
        if (response.status === 500) {
            const errorData = await response.json();
            return {
                success: false,
                error: errorData.message || 'Error interno del servidor. Por favor contacte soporte.',
                detailedError: errorData.error || null
            };
        }
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            return {
                success: false,
                error: errorData?.message || `Error en la generación de factura: ${response.status} ${response.statusText}`,
                detailedError: errorData?.error || null
            };
        }
        
        const data = await response.json();
        console.log('API Response from /facturas:', JSON.stringify(data, null, 2));
        
        // Use the new API response structure
        // The new structure is:
        // {
        //     'factura': {
        //         'serie': string,
        //         'folio': number,
        //         'uuid': string,
        //         'fecha_timbrado': string,
        //         'total': number,
        //     },
        //     'cliente': {
        //         'nombre': string,
        //         'rfc': string,
        //         'email': string
        //     },
        //     'download_links': {
        //         'xml': string,
        //         'pdf': string
        //     }
        // }
        
        // Transform API response to match expected invoice format for the app
        const invoiceData = {
            id: data.factura.folio,
            uuid: data.factura.uuid,
            ticketId: saleData.id,
            fechaTimbrado: data.factura.fecha_timbrado,
            total: data.factura.total || saleData.total,
            customer: {
                ...customerData,
                // Update with possible updated values from the response
                nombre: data.cliente.nombre || customerData.razonSocial,
                rfc: data.cliente.rfc || customerData.rfc,
                email: data.cliente.email || customerData.email
            },
            sale: saleData,
            // Original API fields for backward compatibility
            factura: data.factura,
            cliente: data.cliente,
            // Direct links to the files
            downloadLinks: data.download_links
        };
        
        return {
            success: true,
            data: invoiceData
        };
        
    } catch (error) {
        console.error('Error generating invoice:', error);
        
        // Handle network errors
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            return {
                success: false,
                error: 'No se pudo conectar con el servidor. Verifique su conexión a internet.'
            };
        }
        
        return {
            success: false,
            error: error.message || 'Error inesperado al generar la factura'
        };
    }
};

/**
 * Send invoice via email
 * @param {number} invoiceId - Invoice ID
 * @param {string} email - Recipient email
 * @param {Object} invoiceData - Full invoice data including factura, cliente and download_links
 * @param {string} message - Optional message
 * @returns {Promise<Object>} Email sending result
 */
export const sendInvoiceByEmail = async (invoiceId, email, invoiceData = {}, message = '') => {
    const config = getConfig();
    const API_BASE_URL = config.apiBaseUrl;
    
    console.log(`API Request to: ${API_BASE_URL}/facturas/email`);
    
    try {
        // Use the same structure that was received from the /facturas endpoint
        // Only update the email if needed
        const requestPayload = {
            factura: invoiceData.factura || {
                serie: '',
                folio: invoiceId,
                uuid: invoiceData.uuid || '',
                fecha_timbrado: invoiceData.fechaTimbrado || new Date().toISOString(),
                total: invoiceData.total || 0
            },
            cliente: invoiceData.cliente || {
                nombre: invoiceData.customer?.razonSocial || '',
                rfc: invoiceData.customer?.rfc || '',
                email: email // Override with the email provided
            },
            download_links: invoiceData.downloadLinks || {
                xml: '',
                pdf: ''
            },
            message: message
        };
        
        console.log('Email request payload:', JSON.stringify(requestPayload, null, 2));
        
        const response = await authenticatedFetch(`${API_BASE_URL}/facturas/email`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestPayload)
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return {
                success: false,
                error: errorData.message || 'Error al enviar la factura por correo'
            };
        }
        
        const data = await response.json();
        console.log('API Response from /facturas/email:', JSON.stringify(data, null, 2));
        
        return {
            success: true,
            data: data
        };
        
    } catch (error) {
        console.error('Error sending invoice by email:', error);
        return {
            success: false,
            error: error.message || 'Error inesperado al enviar la factura'
        };
    }
};

/**
 * Resend existing invoice
 * @param {string|number} invoiceId - Invoice ID (could be folio, uuid, or id)
 * @param {string} email - Recipient email  
 * @param {Object} customerData - Optional customer data with updated email
 * @returns {Promise<Object>} Resend result
 */
export const resendInvoice = async (invoiceId, email, customerData = null) => {
    const config = getConfig();
    const API_BASE_URL = config.apiBaseUrl;
    
    console.log(`API Request to: ${API_BASE_URL}/facturas/${invoiceId}/resend`);
    console.log('Resend parameters:', { invoiceId, email, customerData });
    
    try {
        const requestPayload = {
            email: email
        };
        
        // Include customer data if provided for the new API structure
        if (customerData) {
            requestPayload.cliente = {
                nombre: customerData.nombre || '',
                rfc: customerData.rfc || '',
                email: email // Use the provided email
            };
        }
        
        console.log('Resend request payload:', JSON.stringify(requestPayload, null, 2));
        
        const response = await authenticatedFetch(`${API_BASE_URL}/facturas/${invoiceId}/resend`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestPayload)
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                error: errorData.message || `Error al reenviar la factura: ${response.status} ${response.statusText}`
            };
        }
        
        const data = await response.json();
        console.log('API Response from /facturas/{id}/resend:', JSON.stringify(data, null, 2));
        
        return {
            success: true,
            data: data
        };
        
    } catch (error) {
        console.error('Error resending invoice:', error);
        return {
            success: false,
            error: error.message || 'Error inesperado al reenviar la factura'
        };
    }
};